import { useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import type { Utilisateur } from '@/shared/auth/types';
import { depots } from '@/shared/donnees';
import { Avatar, Button, Champ, ErreurFormulaire, Modale, useEnvoi } from '@/shared/ui';
import { identiteDe, LONGUEUR_MAX_IDENTITE, verifierIdentite } from './photo';

/**
 * Le prénom, le nom et la photo d'un compte. Le prénom et le nom sont
 * obligatoires : c'est ce qui signe les actions dans le journal.
 */
export function FormulaireIdentite({
  compte,
  libelleAction = 'Enregistrer',
  onFait,
  onAnnuler,
}: {
  compte: Utilisateur;
  libelleAction?: string;
  onFait: () => void;
  onAnnuler?: (() => void) | undefined;
}) {
  const depart = identiteDe(compte);
  const [prenom, setPrenom] = useState(depart.prenom);
  const [nomFamille, setNomFamille] = useState(depart.nomFamille);
  const [photo, setPhoto] = useState<string | null>(compte.photo ?? null);
  const { envoi, erreur, lancer } = useEnvoi();
  const choix = useRef<HTMLInputElement>(null);

  async function enregistrer() {
    const ok = await lancer(async () => {
      verifierIdentite({ prenom, nomFamille });
      await depots().comptes.modifierIdentite(compte.id, { prenom, nomFamille });
    });
    if (ok) onFait();
  }

  async function changerPhoto(fichier: File | null) {
    const ok = await lancer(() => depots().comptes.changerPhoto(compte.id, fichier));
    if (!ok) return;
    setPhoto(fichier ? URL.createObjectURL(fichier) : null);
  }

  const apercu = `${prenom} ${nomFamille}`.trim() || compte.email;

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void enregistrer();
      }}
      className="flex flex-col gap-4"
    >
      <div className="flex items-center gap-4">
        <Avatar nom={apercu} photo={photo} className="size-16 text-lg" />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            taille="sm"
            disabled={envoi}
            onClick={() => {
              choix.current?.click();
            }}
          >
            <Camera aria-hidden />
            {photo ? 'Changer la photo' : 'Ajouter une photo'}
          </Button>
          {photo ? (
            <Button
              type="button"
              taille="sm"
              variante="discret"
              disabled={envoi}
              onClick={() => void changerPhoto(null)}
            >
              <Trash2 aria-hidden />
              Retirer
            </Button>
          ) : null}
          <input
            ref={choix}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const fichier = e.target.files?.[0] ?? null;
              e.target.value = '';
              if (fichier) void changerPhoto(fichier);
            }}
          />
        </div>
      </div>
      <p className="-mt-2 text-xs text-muted">Photo facultative, réduite à l’envoi.</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Prénom"
          value={prenom}
          maxLength={LONGUEUR_MAX_IDENTITE}
          autoComplete="given-name"
          onChange={(e) => {
            setPrenom(e.target.value);
          }}
        />
        <Champ
          libelle="Nom"
          value={nomFamille}
          maxLength={LONGUEUR_MAX_IDENTITE}
          autoComplete="family-name"
          onChange={(e) => {
            setNomFamille(e.target.value);
          }}
        />
      </div>
      <p className="-mt-2 text-xs text-muted">{compte.email}</p>

      <ErreurFormulaire message={erreur} />

      <div className="flex justify-end gap-2">
        {onAnnuler ? (
          <Button type="button" onClick={onAnnuler}>
            Annuler
          </Button>
        ) : null}
        <Button
          type="submit"
          variante="principal"
          disabled={envoi || !prenom.trim() || !nomFamille.trim()}
        >
          {libelleAction}
        </Button>
      </div>
    </form>
  );
}

/** La fiche identité d'un compte, dans une fenêtre. */
export function ModaleIdentite({
  compte,
  soi,
  onFermer,
  onFait,
}: {
  compte: Utilisateur;
  soi: boolean;
  onFermer: () => void;
  onFait: () => void;
}) {
  return (
    <Modale
      titre={soi ? 'Mon profil' : 'Identité du compte'}
      description={
        soi
          ? 'Votre nom signe vos actions dans le journal.'
          : 'Le nom qui signe les actions de ce compte.'
      }
      onFermer={onFermer}
    >
      <FormulaireIdentite
        compte={compte}
        onAnnuler={onFermer}
        onFait={() => {
          onFait();
          onFermer();
        }}
      />
    </Modale>
  );
}
